import { IconButton, InputAdornment, InputBase, styled } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as React from 'react';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

export const StyledBar = styled('div')(({ theme }) => ({
  backgroundColor: 'rgba(0, 0, 0, 0.06)',
  borderRadius: '30px',
  display: 'flex',
  height: '48px',
  marginBottom: theme.spacing(3),
  padding: theme.spacing(0, 1, 0, 2),
  alignItems: 'center',
  gap: '10px',
  transition: 'background-color 200ms cubic-bezier(0, 0, 0.2, 1) 0ms',

  '&:hover, &:focus-within': {
    backgroundColor: 'rgba(0, 0, 0, 0.12)',
  },

  '& .MuiInputBase-root': {
    backgroundColor: 'transparent',
    overflow: 'visible',

    '&:hover, &:focus-within': {
      backgroundColor: 'transparent',
    },
  },

  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0, 1, 0, 2)
  }
}))

const SEARCH_DEBOUNCE_DELAY = 400

type Props = React.PropsWithChildren<{
  placeholder?: string
  value?: string
  onChange?: (value: string) => void
}>

export default function SearchBar(props: Props) {
  const [t] = useTranslation('common');
  const [localValue, setLocalValue] = useState(props.value ?? '')
  const inputRef = useRef<HTMLInputElement>(null)
  const value = props.value ?? ''

  useEffect(() => {
    setLocalValue(value)
  }, [value])

  useEffect(() => {
    if (localValue === value) {
      return
    }

    const timeout = window.setTimeout(() => {
      props.onChange && props.onChange(localValue)
    }, SEARCH_DEBOUNCE_DELAY)

    return () => {
      window.clearTimeout(timeout)
    }
  }, [localValue, props.onChange, value])

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(String(e.target.value))
  }, [])

  const onClear = useCallback(() => {
    setLocalValue('')
    props.onChange && props.onChange('');
  }, [props.onChange]);

  const onInputMouseDown = useCallback((event: React.MouseEvent<HTMLElement>) => {
    if (event.target === inputRef.current) return
    if (event.target instanceof HTMLElement && event.target.closest('button')) return

    event.preventDefault()
    inputRef.current?.focus()
  }, [])

  return <StyledBar>
    <SearchIcon sx={{ color: 'action.active', fontSize: '28px' }} />
    <InputBase
      inputRef={inputRef}
      placeholder={props.placeholder ? props.placeholder : t('Search...')}
      value={localValue}
      onChange={onChange}
      onMouseDown={onInputMouseDown}
      sx={{
        alignSelf: 'stretch',
        display: 'flex',
        flexGrow: '1',
        height: '100%',
        padding: '0',

        '& .MuiInputBase-input': {
          boxSizing: 'border-box',
          height: '100%',
          padding: '0',
        },

        '& .MuiInputAdornment-positionEnd': {
          height: '100%',
          maxHeight: 'none',
          marginLeft: '0',
        }
      }}
      endAdornment={localValue ? <InputAdornment
        position="end"
        sx={{
          mr: props.children ? 0 : 1,
        }}
      >
        <IconButton
          onClick={onClear}
          edge="end"
        ><ClearIcon /></IconButton>
      </InputAdornment> : null}
    />

    {props.children}
  </StyledBar>
}
